// react
import { useCallback, useMemo } from 'react'
// utils
import { erc20Abi, getAddress, maxUint256, parseUnits } from 'viem'
// types
import { UseApproveParams, UseApproveResult } from '../types'

export function useApprove(params: UseApproveParams): UseApproveResult {
  const { account, accountChainId, targetChainId, tokenInfo, amount } = params

  const { useReadContract, useWriteContract, usePublicClient, useSwitchChain } =
    params.wagmiHooks

  const tokenAddress = tokenInfo?.tokenAddress
  const decimals = tokenInfo?.decimals ?? 18
  const spender = tokenInfo?.contractAddress
  const isBaseToken =
    !tokenAddress ||
    tokenAddress.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'

  // wagmi hooks (must be called at top-level)
  const { switchChainAsync } = useSwitchChain()
  const { writeContractAsync } = useWriteContract()
  const publicClient = usePublicClient()

  // read allowance via wagmi
  const allowanceQuery = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [
      getAddress(account || '') as `0x${string}`,
      spender as `0x${string}`,
    ],
    chainId: targetChainId,
    query: {
      enabled: Boolean(
        !isBaseToken && account && targetChainId && tokenAddress && spender
      ),
    },
  })

  const allowanceValue: bigint | undefined = allowanceQuery?.isSuccess
    ? (allowanceQuery.data as bigint)
    : undefined

  const approve = useMemo(() => {
    if (isBaseToken) return true
    if (!allowanceQuery.isSuccess) {
      return false
    }
    if (allowanceValue === undefined) return false
    if (allowanceValue === 0n) return false
    try {
      const need = parseUnits(
        String(Number(amount || '0') || 0),
        Number(decimals)
      )
      return allowanceValue >= need
    } catch {
      return false
    }
  }, [isBaseToken, allowanceQuery.isSuccess, allowanceValue, amount, decimals])

  const onApprove = useCallback(async () => {
    try {
      // try switch chain when mismatch
      const mismatch =
        targetChainId && accountChainId && +accountChainId !== +targetChainId
      if (mismatch) {
        await switchChainAsync({ chainId: +targetChainId! })
      }

      if (!writeContractAsync || !publicClient || !tokenAddress || !spender) {
        return
      }

      const token = getAddress(tokenAddress)
      const targetSpender = getAddress(spender)

      // reset to 0 first if needed
      const current: bigint = allowanceValue || 0n
      if (current > 0n) {
        const hash = await writeContractAsync({
          abi: erc20Abi,
          address: token,
          functionName: 'approve',
          args: [targetSpender, 0n],
        })
        await publicClient.waitForTransactionReceipt({ hash: hash })
      }

      // approve max
      const hash = await writeContractAsync({
        abi: erc20Abi,
        address: token,
        functionName: 'approve',
        args: [targetSpender, maxUint256],
      })
      await publicClient.waitForTransactionReceipt({ hash })

      // best effort refetch
      if (allowanceQuery?.refetch) await allowanceQuery.refetch()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useApprove] onApprove failed:', e)
    }
  }, [
    targetChainId,
    accountChainId,
    tokenAddress,
    spender,
    allowanceValue,
    allowanceQuery,
    writeContractAsync,
    publicClient,
    switchChainAsync,
  ])

  return { approve, onApprove }
}
